<template>
  <h1>Edit customer labels</h1>
  <div id="page-assemble" class="content-section">
    <div class="page-section">
      <h2>Towel label settings
        <a @click="addItem()" class="float-right btn btn-primary btn-sm">Add new</a>
      </h2>
      <div class="row">
        <div class="col-md-8">
          <span>Find customer: </span>
          <input type="text" v-model="searchValue">
          <easy-data-table
              :loading="loading"
              :headers="headers"
              :items="customerColours"
              :search-field="searchField"
              :search-value="searchValue"
              alternating
              border-cell
              buttons-pagination
          >
            <template #empty-message>
              nothing here for the moment
            </template>

            <template #loading>
              <font-awesome-icon
                  icon="spinner"
                  size="2x"
                  title="Loading"
                  spin
              >
              </font-awesome-icon>
            </template>
            <template #item-fullname="{ firstname, surname }">
              <div class="player-wrapper">
                {{ firstname }} {{ surname }}
              </div>
            </template>
            <template #item-customer_id="{ customer_id }">
              <div class="player-wrapper">
                {{ customer_id }}
              </div>
            </template>
            <template #item-accountcode="{ accountcode }">
              <div class="player-wrapper">
                {{ accountcode }}
              </div>
            </template>
            <template #item-logopath="{ logopath, logopath_publicURL }">
              <div class="player-wrapper">
                <img v-if="logopath" width="20" :src="logopath_publicURL"/> <small>{{ logopath }}</small>
              </div>
            </template>
            <template #item-rgb_colour1="{ rgb_colour1 }">
              <div class="player-wrapper" :style="{'background-color': 'rgb(' + rgb_colour1 + ')'}">
                {{ rgb_colour1 }}
              </div>
            </template>
            <template #item-rgb_colour2="{ rgb_colour2 }">
              <div class="player-wrapper" :style="{'background-color': 'rgb(' + rgb_colour2 + ')'}">
                {{ rgb_colour2 }}
              </div>
            </template>
            <template #item-rgb_colour3="{ rgb_colour3 }">
              <div class="player-wrapper" :style="{'background-color': 'rgb(' + rgb_colour3 + ')'}">
                {{ rgb_colour3 }}
              </div>
            </template>
            <template #item-operation="item">
              <div class="operation-wrapper">
                <font-awesome-icon
                    icon="edit"
                    size="1x"
                    class="operation-icon"
                    title="Edit"
                    @click="editItem(item)">
                </font-awesome-icon>
                <font-awesome-icon
                    icon="trash"
                    size="1x"
                    class="operation-icon delete"
                    title="Delete"
                    @click="deleteItem(item)">
                </font-awesome-icon>

              </div>
            </template>
          </easy-data-table>
        </div>
        <div class="edit-item col-md-4" v-if="isEditing || isAdding">
          <FormKit
              v-if="!submitted && !errorMessage"
              type="form"
              v-model="editingItem"
              @submit="saveCustomerColours"
              :actions="false"
              :config="{
                validationVisibility: 'dirty',
              }"
              form-class="bordered"
              id="formColour"
          >
            <h4 class="text-blue-bold text-left" v-if="isEditing">{{ editingItem.firstname }} {{ editingItem.surname }}</h4>

            <h4 class="text-blue-bold text-left" v-if="isAdding">Add new</h4>
            <div class="formkit-wrapper mb-3"   v-if="isAdding">
              <FormKit
                  v-model="editingItemCustomer_id"
                  name="customer_id"
                  type="autocomplete"
                  label="Customer"
                  label-class="text-left"
                  source-label="All customers"
                  source-empty-message="No customers found"
                  :options="searchCustomers"
                  searchable
                  placeholder="Search customers"
                  required
              />
            </div>
            <img v-if="editingItem.logopath" width="30" :src="editingItem.logopath_publicURL"/>
            <FormKit
                id="logo_file"
                type="file"
                label="Logo"
                name="editing_logo"
                accept=".jpg,.png"
                help="Accepted files: .jpg,.png"
                label-class="text-left"
                @change="appendFile($event.target.name, $event.target.files)"
                clearable="true"
            />
            <FormKit
                type="select"
                label="Colour 1"
                name="rgb_colour1"
                placeholder="Please select the first colour"
                :options="coloursOptions"
                validation="required"
                validation-visibility="submit"
                label-class="text-left"
                input-class="custom-select-sm"
            >
              <template #prefix>
                <div class="color-options" :style="{'background-color': 'rgb(' + editingItem.rgb_colour1 + ')'}"></div>
              </template>
            </FormKit>

            <FormKit
                type="select"
                label="Colour 2"
                name="rgb_colour2"
                placeholder="Please select the second colour"
                :options="coloursOptions"
                validation="required"
                validation-visibility="submit"
                label-class="text-left"
                input-class="custom-select-sm"
            >
              <template #prefix>
                <div class="color-options" :style="{'background-color': 'rgb(' + editingItem.rgb_colour2 + ')'}"></div>
              </template>
            </FormKit>

            <FormKit
                type="select"
                label="Colour 3"
                name="rgb_colour3"
                placeholder="Please select the third colour"
                :options="coloursOptions"
                validation="required"
                validation-visibility="submit"
                label-class="text-left"
                input-class="custom-select-sm"
            >
              <template #prefix>
                <div class="color-options" :style="{'background-color': 'rgb(' + editingItem.rgb_colour3 + ')'}"></div>
              </template>
            </FormKit>

            <FormKit
                v-if="isEditing"
                name="customer_id"
                type="hidden"
                value="{{ editingItem.customer_id }}"
            />

            <FormKit
                type="submit"
                label="Save"
                outer-class="half"
                input-class="$reset btn btn-primary"
            />

            <FormKit
                type="button"
                label="Cancel"
                outer-class="half"
                input-class="$reset btn btn-secondary btn-sm"
                @click="resetColours"
            />
          </FormKit>
          <div v-if="submitted">
            <div role="alert" aria-live="polite" aria-atomic="true" class="alert alert-success">{{successMessage}}</div>
          </div>
          <div v-if="errorMessage">
            <div role="alert" aria-live="polite" aria-atomic="true" class="alert alert-danger">{{errorMessage}}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>

import {onMounted, ref, inject, reactive} from 'vue'
import 'bootstrap/dist/css/bootstrap.min.css';

import CustomerColoursDataService from "@/services/CustomerColoursDataService";

//const formData = ref({})
const isEditing = ref(false);
const isAdding = ref(false);
const submitted = ref(false);
const errorMessage = ref(false);
const successMessage = ref(false);
const loading = ref(true);
const customerColours =  ref([]);
const coloursOptions = inject('globalColoursOptions');
const editingItem = ref({
  firstname: "",
  surname: "",
  logopath: "",
  logopath_publicURL: "",
  rgb_colour1: "",
  rgb_colour2: "",
  rgb_colour3: "",
  customer_id: 0,
});
const currentCustomer = ref('');
const currentIndex = ref('');
const editingItemCustomer_id = ref('');
const searchField = ref(["firstname", "surname", "accountcode", "customer_id", "rgb_colour1", "rgb_colour2", "rgb_colour3"]);
const searchValue = ref("");

const headers =[
  { text: "Name", value: "fullname", sortable: true  },
  { text: "ID", value: "customer_id", sortable: true  },
  { text: "Account", value: "accountcode", sortable: true  },
  { text: "Logo", value: "logopath" },
  { text: "Colour1", value: "rgb_colour1", sortable: true },
  { text: "Colour2", value: "rgb_colour2", sortable: true },
  { text: "Colour2", value: "rgb_colour3", sortable: true },
  { text: "Actions", value: "operation"},
];

const formState = reactive({
  file: null,
  filename: null
});

const appendFile = (name, files) => {
  formState.filename = name;
  formState.file = files[0];
}
/**
 * Populate the form details for customer label settings
 */
const editItem = async (Item) => {

  isEditing.value = true;
  isAdding.value = false;
  emptyCustomerColoursForm();
  editingItem.value.firstname = Item.firstname;
  editingItem.value.surname = Item.surname;
  editingItem.value.logopath = Item.logopath;
  editingItem.value.logopath_publicURL = Item.logopath_publicURL;
  editingItem.value.rgb_colour1 = Item.rgb_colour1;
  editingItem.value.rgb_colour2 = Item.rgb_colour2;
  editingItem.value.rgb_colour3 = Item.rgb_colour3;
  editingItem.value.customer_id = Item.customer_id;
}
const deleteItem = async (Item) => {
  if(confirm("Do you really want to delete?")) {
    CustomerColoursDataService.delete(Item.customer_id)
        .then(response => {
          if (response.data.status == "OK") {
            isEditing.value = true;
            submitted.value = true;
            successMessage.value = 'Item successful deleted!';
            setTimeout(() => {
              isAdding.value = false;
              isEditing.value = false;
              refreshList();
            }, 3000)

          } else {
            errorMessage.value = response.data.message;
          }
          console.log(response);
        })
        .catch(e => {
          console.log(e);
          errorMessage.value = e;
        });
  }
}
/**
 * Updates a customer colours details
 * @returns {Promise<void>}
 */
const saveCustomerColours = async () => {
  let updateObj;

  if (editingItem.value.customer_id == 0)
  {
    editingItem.value.customer_id = editingItemCustomer_id;
  }
  updateObj = {
    firstname: editingItem.value.firstname,
    surname: editingItem.value.surname,
    logopath: editingItem.value.logopath,
    logopath_publicURL: editingItem.value.logopath_publicURL,
    rgb_colour1: editingItem.value.rgb_colour1,
    rgb_colour2: editingItem.value.rgb_colour2,
    rgb_colour3: editingItem.value.rgb_colour3,
    customer_id: editingItem.value.customer_id
  }


  let formData = new FormData();
  formData.append('file', formState.file);
  Object.keys(updateObj).forEach((key) => {
    formData.append(`${key}`, `${updateObj[key]}`);
  });
  CustomerColoursDataService.save(formData)
      .then(response => {
        if (response.data.status == "OK"){
          submitted.value=true;
          successMessage.value = 'Submission successful!';
          setTimeout(() => {
            isAdding.value = false;
            isEditing.value = false;
            refreshList();
          }, 3000)
        } else if (response.data.status == "DUPLICATE") {
          isAdding.value = true;
          submitted.value = false;
          successMessage.value = '';
          errorMessage.value=response.data.message;

        } else {
          errorMessage.value=response.data.status + " Please contact the administrator.";
          setTimeout(() => {
            isAdding.value = false;
            isEditing.value = false;
            errorMessage.value = false;
            resetColours();
          }, 3000)
        }
        console.log(response);
      })
      .catch(e => {
        console.log(e);
        errorMessage.value=e;
        setTimeout(() => {
          isAdding.value = false;
          isEditing.value = false;
          errorMessage.value = false;
          resetColours();
        }, 3000)
      });
}
/*
const preventSubmit = (e) => {
  e.preventDefault();
  return false;
}*/

/**
 * Perform an API request to find customers based on keyword used on autocomplete
 * @param search
 * @returns {Promise<{label: string, value: *}[]|*[]>}
 */
const searchCustomers = async ({search}) => {
  try {
    const response = await CustomerColoursDataService.findByCustomer(search);
    const res = response.data.customers;

    // Map the data and return it
    return res.map((entry) => ({
      label: entry.text + " " + entry.accountcode,
      value: entry.id,
    }));
  } catch (e) {
    console.log(e);
    return [];
  }
}
const emptyCustomerColoursForm = async () => {
  editingItem.value.firstname = '';
  editingItem.value.surname = '';
  editingItem.value.logopath = '';
  editingItem.value.logopath_publicURL = '';
  editingItem.value.rgb_colour1 = '';
  editingItem.value.rgb_colour2 = '';
  editingItem.value.rgb_colour3 = '';
  editingItem.value.customer_id = '';
  successMessage.value = '';
  errorMessage.value = '';
  editingItem.value.file = '';
  //document.getElementById('editing_logo')[0].file = null
  //document.getElementById('editing_logo').value= '';
  //document.getElementById('formColour').reset();

}
const resetColours = async () =>  {
  emptyCustomerColoursForm();

  isAdding.value = false;
  isEditing.value = false;
}
const refreshList = async () =>  {
  retrieveCustomerColours();
  currentCustomer.value = null;
  currentIndex.value = -1;
}
function retrieveCustomerColours() {
  submitted.value = false;
  errorMessage.value = false;
  CustomerColoursDataService.getAll()
      .then(response => {
        customerColours.value = response.data.customers;
        //convert object to array
        // this.customerColours = Object.entries(response.data.customers);
        console.log(response.data.customers);
        loading.value = false;
      })
      .catch(e => {
        console.log(e);
      });
}


function addItem() {
  isAdding.value = true;
  isEditing.value = false;
  emptyCustomerColoursForm();
}
onMounted(() => {
  retrieveCustomerColours();
})
</script>


<style scoped>

.half {
  float: left;
  width: 50%;
}
.formkit-option {
  display: flex;
  align-items: center;
}
.color-options {
  width: 30px;
  height: 30px;
}
[data-invalid] .formkit-inner {
  border-color: var(--red-color);
  box-shadow: 0 0 0 1px var(--red-color);
}

[data-complete] .formkit-inner {
  border-color: var(--red-color);
  box-shadow: 0 0 0 1px var(--green-color);
}
[data-complete] .formkit-inner::after {
  content: '✅';
  display: block;
  padding: 0.5em;
}
.formkit-form.bordered {
  padding: 0.5rem 1.5rem;
  margin-right: 0;
  margin-left: 0;
  border: 1px solid var(--form-border-color);
  box-shadow: 0 2px 3px var(--form-shadow-color);
  display: block;
  float: left;
  width: 100%;
}
.custom-select-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  padding-left: 0.5rem;
  font-size: .875rem;
}
</style>